import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import DashboardLayout from "@/layout/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Analyzer from "@/pages/Analyzer";
import ImageToExcel from "@/pages/ImageToExcel";
import Workspace from "@/pages/Workspace";
import Documents from "@/pages/Documents";
import Analyses from "@/pages/Analyses";
import Settings from "@/pages/Settings";
import AuditLogs from "@/pages/AuditLogs";
import Help from "@/pages/Help";
import Banks from "@/pages/crm/Banks";
import Masters from "@/pages/crm/Masters";
import Pipeline from "@/pages/crm/Pipeline";
import LeadDetail from "@/pages/crm/LeadDetail";
import Payouts from "@/pages/crm/Payouts";
import CrmReports from "@/pages/crm/Reports";
import FollowUps from "@/pages/crm/FollowUps";
import Tasks from "@/pages/crm/Tasks";
import Approvals from "@/pages/crm/Approvals";
import Disbursement from "@/pages/crm/Disbursement";
import UsersAdmin from "@/pages/crm/Users";
import RolesPermissions from "@/pages/crm/RolesPermissions";
import ImportExport from "@/pages/crm/ImportExport";
import Integrations from "@/pages/crm/Integrations";
import ManagementDashboard from "@/pages/crm/ManagementDashboard";
import Enquiries from "@/pages/crm/Enquiries";
import PublicEnquiry from "@/pages/PublicEnquiry";
import { Loader2 } from "lucide-react";

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex h-screen items-center justify-center" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/enquiry/new" element={<PublicEnquiry />} />
          <Route element={<Protected><DashboardLayout /></Protected>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/analyzer" element={<Analyzer />} />
            <Route path="/image-to-excel" element={<ImageToExcel />} />
            <Route path="/workspace/:jobId" element={<Workspace />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/crm/dashboard" element={<ManagementDashboard />} />
            <Route path="/crm/enquiries" element={<Enquiries />} />
            <Route path="/crm/pipeline" element={<Pipeline />} />
            <Route path="/crm/leads/:leadId" element={<LeadDetail />} />
            <Route path="/crm/followups" element={<FollowUps />} />
            <Route path="/crm/tasks" element={<Tasks />} />
            <Route path="/crm/approvals" element={<Approvals />} />
            <Route path="/crm/disbursement" element={<Disbursement />} />
            <Route path="/admin/users" element={<UsersAdmin />} />
            <Route path="/admin/roles" element={<RolesPermissions />} />
            <Route path="/admin/import-export" element={<ImportExport />} />
            <Route path="/crm/banks" element={<Banks />} />
            <Route path="/crm/masters" element={<Masters />} />
            <Route path="/crm/payouts" element={<Payouts />} />
            <Route path="/crm/reports" element={<CrmReports />} />
            <Route path="/crm/integrations" element={<Integrations />} />
            <Route path="/analyses" element={<Analyses />} />
            <Route path="/reports" element={<Analyses reports />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/audit" element={<AuditLogs />} />
            <Route path="/help" element={<Help />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
